// Globals
import { isFulfilled } from '@reduxjs/toolkit';
import { useCallback } from 'react';

// Store
import { useAppDispatch } from '@/store';
import { addFilesToOrder } from '../feature';

// Modules
import { uploadOrderFile } from '@/modules/Upload';

// Components
import { showErrorToast } from '@/components';

// Models
import { useBoolean } from '@/hooks';
import { FileVariant } from '@/models/file.model';
import { FileType } from '@/models/uploads.model';

export type IUploadFilesDTO = {
  orderId: string;
  files: {
    file: File;
    type: FileType;
  }[];
  type: FileVariant;
};

export const useUploadOrderFiles = () => {
  const dispatch = useAppDispatch();
  const { value: isLoading, setTrue, setFalse } = useBoolean();
  const handleUpload = useCallback(async ({ files, orderId, type }: IUploadFilesDTO) => {
    setTrue();
    const requests = files.map((file) => dispatch(uploadOrderFile({ file: file.file, id: orderId, type: file.type })));
    const result = await Promise.all(requests);
    const filesResult = result.reduce((acc, item) => {
      if (isFulfilled(item)) {
        acc.push({
          name: item.meta.arg.file.name,
          link: item.payload.asset_public_url,
        });
      }
      return acc;
    }, [] as { name: string; link: string }[]);

    if (!filesResult.length) {
      showErrorToast({
        message: 'Something went wrong',
      });
      setFalse();
      return;
    }

    await dispatch(
      addFilesToOrder({
        orderId,
        files: filesResult,
        type,
      }),
    );

    setFalse();
  }, []);

  return {
    uploadFiles: handleUpload,
    isUploading: isLoading,
  };
};
